// Teams & Permissions Management System
// Unified interface for managing teams, users, and access permissions

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Users, Plus } from 'lucide-react';
import { TeamsTable } from '@/components/teams/TeamsTable';
import { UsersTable } from '@/components/teams/UsersTable';
import { TeamDetailView } from '@/components/teams/TeamDetailView';
import { CreateTeamView } from '@/components/teams/CreateTeamView';
import { DemoModeBadge } from '@/components/ui/demo-mode-badge';
import { useMode } from '@/providers/ModeProvider';
import { useTeams } from '@/hooks/use-teams';
import { useUsers } from '@/hooks/use-users';
import { demoPermissions } from '@/data/demo-teams';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import type { Team, TeamFilters, UserFilters, TeamCreateDto } from '@/types/teams';

export default function TeamsPermissionsPage() {
  const { toast } = useToast();
  const { isDemo } = useMode();
  const location = useLocation();
  const teamsHook = useTeams();
  const usersHook = useUsers();
  
  // State
  const [activeTab, setActiveTab] = useState('teams');
  const [teamFilters, setTeamFilters] = useState<TeamFilters>({});
  const [userFilters, setUserFilters] = useState<UserFilters>({});
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [showCreateView, setShowCreateView] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, [teamsHook.loadTeams, usersHook.loadUsers]);

  const loadData = async () => {
    try {
      await Promise.all([
        teamsHook.loadTeams(teamFilters),
        usersHook.loadUsers(userFilters),
      ]);
    } catch (error) {
      console.error('Failed to load teams data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load teams and users',
        variant: 'destructive',
      });
    }
  };

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadData, location.pathname);

  const handleTeamClick = (team: Team) => {
    setSelectedTeamId(team.id);
  };

  const handleBackToList = () => {
    setSelectedTeamId(null);
    setShowCreateView(false);
  };

    const handleCreateTeam = async (data: TeamCreateDto) => {
      try {
        await teamsHook.createTeam(data);
        setShowCreateView(false);
      
        toast({
        title: 'Success',
        description: `Team "${data.name}" created successfully`,
      });
    } catch (error) {
      console.error('Failed to create team:', error);
      toast({
        title: 'Error',
        description: 'Failed to create team. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateTeam = async (teamId: string, updates: Partial<Team>) => {
    try {
      await teamsHook.updateTeam(teamId, updates);
      
      toast({
        title: 'Success',
        description: 'Team updated successfully',
      });
    } catch (error) {
      console.error('Failed to update team:', error);
      toast({
        title: 'Error',
        description: 'Failed to update team. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    try {
      const team = teamsHook.teams.find(t => t.id === teamId);
      await teamsHook.deleteTeam(teamId);
      setSelectedTeamId(null);
      
      toast({
        title: 'Success',
        description: `Team "${team?.name}" deleted successfully`,
      });
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete team. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteTeams = async (teamIds: string[]) => {
    try {
      await teamsHook.deleteTeams(teamIds);
      
      toast({
        title: 'Success',
        description: `${teamIds.length} team${teamIds.length > 1 ? 's' : ''} deleted successfully`,
      });
    } catch (error) {
      console.error('Failed to delete teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete teams. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleUpdateTeamPermissions = async (teamId: string, grantedPermissions: string[]) => {
    try {
      await teamsHook.updateTeamPermissions(teamId, grantedPermissions);
      
      toast({
        title: 'Success',
        description: 'Team permissions updated successfully',
      });
    } catch (error) {
      console.error('Failed to update team permissions:', error);
      toast({
        title: 'Error',
        description: 'Failed to update team permissions. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const selectedTeam = teamsHook.teams.find(t => t.id === selectedTeamId);
  const loading = teamsHook.loading || usersHook.loading;

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="border-b bg-card p-6">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-full" />
        </div>
      </div>
    );
  }

  // Show create team view
  if (showCreateView) {
    return (
      <CreateTeamView
        teams={teamsHook.teams}
        users={usersHook.users}
        permissions={demoPermissions}
        onBack={handleBackToList}
        onCreate={handleCreateTeam}
      />
    );
  }

  // Show team detail view if a team is selected
  if (selectedTeam) {
    return (
      <TeamDetailView
        team={selectedTeam}
        teams={teamsHook.teams}
        users={usersHook.users}
        permissions={demoPermissions}
        onBack={handleBackToList}
        onUpdate={handleUpdateTeam}
        onDelete={handleDeleteTeam}
        onUpdatePermissions={handleUpdateTeamPermissions}
      />
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header - Following Compliance Page Pattern */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            {/* Title */}
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold tracking-tight">Team Settings</h1>
              {isDemo && <DemoModeBadge />}
            </div>
            
            {/* Subtitle */}
            <p className="text-sm text-muted-foreground mt-1">
              Manage teams, assign users, and control application access
            </p>
          </div>
          
                    {/* Action Button */}
                    <div className="flex items-center gap-2">
                      <EvoFeatureFeedbackCollector feature="Teams & Permissions" />
                      <Button onClick={() => setShowCreateView(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Team
                      </Button>
                    </div>
                  </div>
                </div>

            {/* Tab Bar - Enhanced Enterprise Design */}
      <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
            <TabsTrigger 
              value="teams"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Teams
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Users
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'teams' && (
          <TeamsTable
            teams={teamsHook.teams}
            allUsers={usersHook.users}
            permissions={demoPermissions}
            filters={teamFilters}
            onFiltersChange={setTeamFilters}
            onTeamClick={handleTeamClick}
            onDeleteTeams={handleDeleteTeams}
            onRefresh={loadData}
          />
        )}
        
        {activeTab === 'users' && (
          <UsersTable
            users={usersHook.users}
            teams={teamsHook.teams}
            filters={userFilters}
            onFiltersChange={setUserFilters}
            onRefresh={loadData}
            onRefreshUsers={usersHook.refreshUsers}
            refreshing={usersHook.refreshing}
          />
        )}
      </div>
    </div>
  );
}
